/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { roundTo } from "@/lib/utils/domain/calculation-helpers";
import type { PaginationInfo } from "@/lib/utils/responses";

export type EstimatesApiMetrics = {
  statusCounts?: Record<string, number>;
  totalValue?: number;
  wonValue?: number;
  winRate?: number;
  avgEstimateValue?: number;
};

export interface EstimateMetrics {
  total: number;
  active: number;
  draft: number;
  completed: number;
  totalValue: number;
  winRate: number;
  wonValue: number;
  avgEstimateValue: number;
  statusCounts: Record<string, number>;
}

const DRAFT_STATUSES = ["DRAFT", "IN_REVIEW"];
const ACTIVE_STATUSES = ["APPROVED", "SENT_TO_CLIENT"];
const COMPLETED_STATUSES = ["WON", "LOST"];

export function normalizeEstimatePagination(
  pagination: PaginationInfo | undefined,
  page: number,
  limit: number,
  dataLength: number | undefined,
): PaginationInfo {
  if (pagination) {
    return pagination;
  }

  const total = dataLength ?? 0;
  const pages = Math.max(1, Math.ceil(total / limit));

  return {
    page,
    limit,
    total,
    pages,
    hasMore: page < pages,
    hasPrevious: page > 1,
  };
}

export function deriveEstimateMetrics(
  metrics: EstimatesApiMetrics | undefined,
  pagination: PaginationInfo,
): EstimateMetrics {
  const statusCounts = { ...(metrics?.statusCounts ?? {}) };
  const totalFromCounts = Object.values(statusCounts).reduce(
    (sum, count) => sum + count,
    0,
  );
  const total = pagination?.total ?? totalFromCounts;

  const sumStatuses = (statuses: string[]) =>
    statuses.reduce((sum, status) => sum + (statusCounts[status] ?? 0), 0);

  const draft = sumStatuses(DRAFT_STATUSES);
  const active = sumStatuses(ACTIVE_STATUSES);
  const completed = sumStatuses(COMPLETED_STATUSES);

  const totalValue = metrics?.totalValue ?? 0;
  const wonValue = metrics?.wonValue ?? 0;
  const winRate = metrics?.winRate ?? 0;
  const avgEstimateValue =
    metrics?.avgEstimateValue ??
    (total > 0 ? roundTo(totalValue / total, 2) : 0);

  return {
    total,
    active,
    draft,
    completed,
    totalValue,
    winRate,
    wonValue,
    avgEstimateValue,
    statusCounts,
  };
}
